import { FC, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { glClassificationApi } from '../../../resources/hooks/api/glClassificationApi';

interface Props {
    branchId: number;
    onDone: () => void;
}

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];


const QBDReconciliationCutoffModal: FC<Props> = ({ branchId, onDone }) => {
    const { glReconcileQbdBackfill } = glClassificationApi();
    const { t } = useTranslation('scenes.thirdparty.quickbook.QBCOAClassification');

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: currentYear - 2015 + 2 }, (_, i) => 2015 + i);

    const [year, setYear] = useState<number>(currentYear);
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const handleSave = () => {
        setSubmitting(true);
        glReconcileQbdBackfill(
            { branch_id: branchId, year, month },
            (message: string) => {
                setSubmitting(false);
                toast.success(message || t('reconciliation_cutoff.success', 'Bank reconciliation updated.'));
                onDone();
            },
            (message: string) => {
                setSubmitting(false);
                toast.error(message || t('reconciliation_cutoff.error', 'Could not update bank reconciliation.'));
            }
        );
    };

    return (
        <Modal show centered onHide={onDone} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('reconciliation_cutoff.title', 'Bank Reconciliation')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    {t(
                        'reconciliation_cutoff.description',
                        'In QuickBooks Desktop, through which month is bank reconciliation complete? Every bank account will be marked reconciled through that month.'
                    )}
                </p>
                <div className="d-flex gap-2">
                    <Form.Group className="flex-grow-1">
                        <Form.Label>{t('reconciliation_cutoff.month', 'Month')}</Form.Label>
                        <Form.Select
                            value={month}
                            onChange={(e) => setMonth(Number(e.target.value))}
                            disabled={submitting}
                        >
                            {MONTH_NAMES.map((name, idx) => (
                                <option key={idx + 1} value={idx + 1}>{t(`reconciliation_cutoff.monthNames.${name.toLowerCase()}`, name)}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="flex-grow-1">
                        <Form.Label>{t('reconciliation_cutoff.year', 'Year')}</Form.Label>
                        <Form.Select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            disabled={submitting}
                        >
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onDone} disabled={submitting}>
                    {t('reconciliation_cutoff.skip', 'Skip for now')}
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={submitting}>
                    {submitting ? t('reconciliation_cutoff.saving', 'Saving...') : t('reconciliation_cutoff.save', 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default QBDReconciliationCutoffModal;
